import { mutate as swrMutate } from 'swr';
import { useState } from 'react';
import { useAuthStore } from './useAuth';
import toast from 'react-hot-toast';

interface CreatePOPayload {
    customerPoNumber: string;
    clientName: string;
    customerId?: string;    // Explicit customer linkage when selected from autocomplete
    type: 'inbound' | 'outbound';
    totalSkids: number;
    totalWeight?: number; // Added
    description?: string;
    expectedArrival?: string;
    // Phase INV-1: Receiving & Putaway
    required_temperature?: string;
    case_count?: number;
    item_description?: string;
    handling_type?: string;
    putaway_zone?: string;
    // V1.3A: PO Arrival Requirements
    dueDate?: string;
    priority?: 'standard' | 'urgent' | 'critical';
}

interface UseCreatePOResult {
    mutate: (data: CreatePOPayload, options?: { onSuccess?: (data: any) => void }) => Promise<void>;
    isLoading: boolean;
    error: string | null;
}

export function useCreatePO(): UseCreatePOResult {
    const { token } = useAuthStore();
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const mutate = async (payload: CreatePOPayload, options?: { onSuccess?: (data: any) => void }) => {
        setIsLoading(true);
        setError(null);

        try {
            const response = await fetch('/api/warehouse-pos', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to create PO');
            }

            const data = await response.json();

            // Optimistic SWR UI Update
            if (token) {
                const swrKey = ['/api/warehouse-pos', token];
                // Optimistically insert new PO at the top
                swrMutate(swrKey, (current: any) => {
                    return current ? [data, ...current] : [data];
                }, { revalidate: false });

                // Trigger background revalidation to ensure data consistency
                swrMutate(swrKey);
            }

            // "Electric Purple" Success Toast as requested
            toast.success('PO Created Successfully', {
                style: {
                    background: '#1e1e2e', // Dark background
                    color: '#c084fc', // Electric Purple text
                    border: '1px solid #7c3aed' // Purple border
                },
                icon: '📋'
            });

            // Signal dashboard to refresh onboarding checklist immediately
            window.dispatchEvent(new CustomEvent('baywhiz:po-created'));

            if (options?.onSuccess) {
                options.onSuccess(data);
            }

        } catch (err: any) {
            setError(err.message);
            // Show tailored error for duplicates
            if (err.message?.toLowerCase().includes('duplicate po number') || err.message?.toLowerCase().includes('already exists')) {
                toast.error('PO number already exists. Use Auto-Gen or enter a different number.', {
                    duration: 5000,
                });
            } else {
                toast.error(err.message);
            }
        } finally {
            setIsLoading(false);
        }
    };

    return { mutate, isLoading, error };
}
